import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
	FiAlertTriangle,
	FiArrowLeft,
	FiCheckCircle,
	FiChevronDown,
	FiChevronUp,
	FiClock,
	FiCreditCard,
	FiExternalLink,
	FiHelpCircle,
	FiMail,
	FiSearch,
	FiShield,
	FiSmartphone,
	FiUser,
	FiUsers,
} from "react-icons/fi";
import { HiTicket } from "react-icons/hi";
import { IconType } from "react-icons";
import { usePageTitle } from "../hooks/usePageTitle";

type HelpCategoryId =
	| "getting-started"
	| "tickets"
	| "payments"
	| "account"
	| "organizer"
	| "troubleshooting";

type CategoryFilter = "all" | HelpCategoryId;

interface HelpCategory {
	id: HelpCategoryId;
	label: string;
	description: string;
	icon: IconType;
}

interface FaqItem {
	id: string;
	category: HelpCategoryId;
	question: string;
	answer: string[];
	tags: string[];
}

interface GuideStep {
	title: string;
	description: string;
}

interface TroubleshootingCard {
	title: string;
	symptoms: string[];
	actions: string[];
}

const helpCategories: HelpCategory[] = [
	{
		id: "getting-started",
		label: "はじめての方へ",
		description: "サービスの基本と最短導線",
		icon: FiHelpCircle,
	},
	{
		id: "tickets",
		label: "チケット",
		description: "購入後の確認・入場方法",
		icon: HiTicket,
	},
	{
		id: "payments",
		label: "決済",
		description: "支払い・返金に関する案内",
		icon: FiCreditCard,
	},
	{
		id: "account",
		label: "アカウント",
		description: "ログインや登録情報の管理",
		icon: FiUser,
	},
	{
		id: "organizer",
		label: "主催者向け",
		description: "イベント作成から入場管理まで",
		icon: FiUsers,
	},
	{
		id: "troubleshooting",
		label: "トラブル対応",
		description: "よくある不具合の対処手順",
		icon: FiAlertTriangle,
	},
];

const attendeeSteps: GuideStep[] = [
	{
		title: "イベントを探す",
		description:
			"「イベント一覧」またはヘッダー検索から、日程・会場・キーワードで目的のイベントを見つけます。",
	},
	{
		title: "チケットを選ぶ",
		description:
			"券種ごとに枚数を選択します。販売期間・在庫・注意事項を確認してから進みます。",
	},
	{
		title: "注文内容を確認",
		description:
			"ログイン後、購入内容と金額を確認します。無料チケットもこの画面で確定します。",
	},
	{
		title: "決済を完了",
		description:
			"クレジットカードまたはコンビニ決済で支払います。成功後はマイページに反映されます。",
	},
	{
		title: "当日はQRチケットで入場",
		description:
			"「マイページ > マイチケット」からQRを表示し、会場で提示してください。",
	},
];

const organizerSteps: GuideStep[] = [
	{
		title: "主催者登録を申請",
		description:
			"主催者登録ページから必要情報を入力し、利用規約への同意後に申請します。",
	},
	{
		title: "イベントを作成",
		description:
			"基本情報・開催日時・会場・チケット種別・注意事項を設定して公開します。",
	},
	{
		title: "販売状況を確認",
		description:
			"ダッシュボードで販売枚数や売上の推移を確認し、必要に応じて情報を更新します。",
	},
	{
		title: "当日チェックインを実施",
		description:
			"「入場QRコード読み取り」で参加者のQRコードを読み取り、入場ステータスを更新します。",
	},
	{
		title: "売上管理と入金申請",
		description:
			"売上画面で精算対象を確認し、必要なタイミングで入金申請を行います。",
	},
];

const faqItems: FaqItem[] = [
	{
		id: "faq-purchase-flow",
		category: "getting-started",
		question: "チケット購入までの最短手順を教えてください。",
		answer: [
			"イベント詳細ページで券種と枚数を選択し、購入手続きへ進んでください。",
			"ログイン後に注文内容を確認し、決済が完了するとマイページへチケットが反映されます。",
		],
		tags: ["購入", "流れ", "手順", "はじめて"],
	},
	{
		id: "faq-ticket-display",
		category: "tickets",
		question: "購入したチケットはどこで確認できますか？",
		answer: [
			"ログイン後、「マイページ > マイチケット」で確認できます。",
			"イベント単位でチケットがまとまり、当日はQRコードを表示して入場します。",
		],
		tags: ["マイページ", "QR", "表示", "チケット"],
	},
	{
		id: "faq-ticket-missing",
		category: "tickets",
		question: "決済したのにチケットが表示されません。",
		answer: [
			"まず「購入履歴」に注文が反映されているか確認してください。",
			"反映がない場合は通信エラーまたは決済未完了の可能性があります。時間をおいて再読み込みし、改善しない場合は注文情報を添えてお問い合わせください。",
		],
		tags: ["表示されない", "購入履歴", "注文", "反映"],
	},
	{
		id: "faq-cancel-policy",
		category: "tickets",
		question: "購入後にキャンセル・返金はできますか？",
		answer: [
			"原則として購入者都合のキャンセル・返金はできません。",
			"イベント中止や主催者都合の変更など、返金対象となる条件は各イベントの案内と利用規約をご確認ください。",
		],
		tags: ["キャンセル", "返金", "規約"],
	},
	{
		id: "faq-transfer",
		category: "tickets",
		question: "チケットを別のユーザーへ譲渡できますか？",
		answer: [
			"本サービスではユーザー間での公式譲渡機能は提供していません。",
			"不正転売や第三者へのスクリーンショット共有はトラブルの原因となるため行わないでください。",
		],
		tags: ["譲渡", "転売", "スクリーンショット"],
	},
	{
		id: "faq-payment-method",
		category: "payments",
		question: "対応している支払い方法は何ですか？",
		answer: [
			"現在はクレジットカード決済とコンビニ決済に対応しています。",
			"イベントや購入条件により利用可能な決済方法が異なる場合があります。",
		],
		tags: ["決済", "カード", "コンビニ", "支払い方法"],
	},
	{
		id: "faq-payment-fail",
		category: "payments",
		question: "決済エラーが出る場合はどうすればよいですか？",
		answer: [
			"カード利用枠・3Dセキュア認証・有効期限・入力情報の誤りを確認してください。",
			"同一注文で連続失敗した場合は時間を空け、ブラウザ再起動後に再試行してください。",
		],
		tags: ["決済失敗", "エラー", "3Dセキュア"],
	},
	{
		id: "faq-free-ticket",
		category: "payments",
		question: "無料チケットの購入でも決済は必要ですか？",
		answer: [
			"無料チケットは支払い処理なしで予約確定します。",
			"確定後は通常の有料チケットと同様にマイページで管理できます。",
		],
		tags: ["無料", "予約", "決済不要"],
	},
	{
		id: "faq-login",
		category: "account",
		question: "ログインできないときの確認ポイントは？",
		answer: [
			"ブラウザのCookie制限や拡張機能が認証を妨げる場合があります。シークレットモード以外で再試行してください。",
			"複数タブで同時にログイン操作している場合は、一度全タブを閉じてから再度ログインしてください。",
		],
		tags: ["ログイン", "認証", "Cookie"],
	},
	{
		id: "faq-account-edit",
		category: "account",
		question: "氏名や連絡先などアカウント情報はどこで変更できますか？",
		answer: [
			"ヘッダーのユーザーメニューからアカウント編集ページへ移動できます。",
			"イベント参加に必要な連絡先は最新状態に保ってください。",
		],
		tags: ["プロフィール", "変更", "連絡先"],
	},
	{
		id: "faq-security",
		category: "account",
		question: "セキュリティ上、気をつけるべきことはありますか？",
		answer: [
			"チケットQRの共有や不審なリンクのクリックは避けてください。",
			"公共端末利用後は必ずログアウトし、ブラウザ保存情報を削除してください。",
		],
		tags: ["セキュリティ", "不正利用", "QR"],
	},
	{
		id: "faq-organizer-start",
		category: "organizer",
		question: "主催者として販売を始めるには何が必要ですか？",
		answer: [
			"主催者登録の完了後、イベント作成画面から公開設定まで行うと販売開始できます。",
			"開催日時、会場情報、券種、注意事項は公開前に必ず最終確認してください。",
		],
		tags: ["主催者登録", "販売開始", "イベント作成"],
	},
	{
		id: "faq-organizer-edit",
		category: "organizer",
		question: "公開後のイベント情報は編集できますか？",
		answer: [
			"主催者ダッシュボードのイベント編集画面から変更できます。",
			"販売済みチケットに影響する重要変更は、購入者への告知を必ず行ってください。",
		],
		tags: ["イベント編集", "公開後", "主催者"],
	},
	{
		id: "faq-checkin-error",
		category: "organizer",
		question: "QR読み取りがうまく動作しない場合は？",
		answer: [
			"まずブラウザのカメラ権限を許可し、背面カメラへの切り替えを試してください。",
			"光量不足やピント不良で読み取れない場合は、手動入力モードでトークン確認を行ってください。",
		],
		tags: ["QR", "読み取り", "カメラ", "チェックイン"],
	},
	{
		id: "faq-payout",
		category: "organizer",
		question: "売上金の入金はいつ行われますか？",
		answer: [
			"入金処理は主催者側の入金申請後に実行されます。",
			"反映タイミングは金融機関営業日や審査状況で前後するため、急ぎの場合は余裕を持って申請してください。",
		],
		tags: ["売上", "入金", "主催者"],
	},
	{
		id: "faq-browser",
		category: "troubleshooting",
		question: "推奨ブラウザはありますか？",
		answer: [
			"最新版のChrome、Safari、Firefox、Edgeの利用を推奨します。",
			"古いブラウザや企業内プロキシ環境では決済・カメラ機能が正常動作しない場合があります。",
		],
		tags: ["ブラウザ", "推奨環境", "不具合"],
	},
	{
		id: "faq-day-of-event",
		category: "troubleshooting",
		question: "イベント当日に入場できない場合は？",
		answer: [
			"まずマイチケットのステータスとQR表示可否を確認してください。",
			"当日運営の判断が必要なケースは、会場スタッフまたは主催者案内に従って対応してください。",
		],
		tags: ["当日", "入場", "会場"],
	},
	{
		id: "faq-support",
		category: "troubleshooting",
		question: "問い合わせ時に何を伝えれば早く解決できますか？",
		answer: [
			"発生日時、イベント名、注文番号、利用ブラウザ、表示されたエラーメッセージをお知らせください。",
			"スクリーンショットがあると原因特定が速くなります。",
		],
		tags: ["問い合わせ", "サポート", "エラー"],
	},
];

const troubleshootingCards: TroubleshootingCard[] = [
	{
		title: "ログインできない",
		symptoms: [
			"ログイン画面へ戻される",
			"認証後にページが更新されない",
		],
		actions: [
			"ブラウザのCookie設定を有効化する",
			"拡張機能を一時無効化して再ログインする",
			"別ブラウザで同じ操作を試す",
		],
	},
	{
		title: "決済が失敗する",
		symptoms: [
			"支払いボタン押下後にエラー表示",
			"購入完了画面へ遷移しない",
		],
		actions: [
			"カード情報と3Dセキュア認証を確認する",
			"通信状態が安定した環境で再実行する",
			"重複購入防止のため連打せず1回ずつ操作する",
		],
	},
	{
		title: "QRチケットを表示できない",
		symptoms: ["マイチケットに対象イベントが出ない", "QRモーダルが開かない"],
		actions: [
			"購入履歴に注文があるか確認する",
			"ページ再読み込み後に再表示する",
			"解消しない場合は注文情報を添えて問い合わせる",
		],
	},
	{
		title: "主催者のQR読み取りができない",
		symptoms: ["カメラが起動しない", "ピントが合わず読み取りできない"],
		actions: [
			"ブラウザのカメラ権限を許可する",
			"背面カメラへ切り替えて再試行する",
			"手動トークン入力で照合する",
		],
	},
];

const HelpPage: React.FC = () => {
	usePageTitle("ヘルプセンター");

	const [searchQuery, setSearchQuery] = useState("");
	const [activeCategory, setActiveCategory] = useState<CategoryFilter>("all");
	const [openFaqId, setOpenFaqId] = useState<string | null>(faqItems[0]?.id ?? null);

	const normalizedQuery = searchQuery.trim().toLowerCase();

	const faqCountByCategory = useMemo(() => {
		return faqItems.reduce<Record<HelpCategoryId, number>>(
			(acc, item) => {
				acc[item.category] += 1;
				return acc;
			},
			{
				"getting-started": 0,
				tickets: 0,
				payments: 0,
				account: 0,
				organizer: 0,
				troubleshooting: 0,
			}
		);
	}, []);

	const filteredFaqItems = useMemo(() => {
		return faqItems.filter((item) => {
			if (activeCategory !== "all" && item.category !== activeCategory) {
				return false;
			}

			if (!normalizedQuery) {
				return true;
			}

			const searchable = [item.question, ...item.answer, ...item.tags]
				.join(" ")
				.toLowerCase();
			return searchable.includes(normalizedQuery);
		});
	}, [activeCategory, normalizedQuery]);

	useEffect(() => {
		if (filteredFaqItems.length === 0) {
			setOpenFaqId(null);
			return;
		}

		if (!filteredFaqItems.some((item) => item.id === openFaqId)) {
			setOpenFaqId(filteredFaqItems[0].id);
		}
	}, [filteredFaqItems, openFaqId]);

	const toggleFaq = (faqId: string) => {
		setOpenFaqId((prev) => (prev === faqId ? null : faqId));
	};

	return (
		<div className="max-w-6xl mx-auto space-y-8">
			<div>
				<Link
					to="/"
					className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
				>
					<FiArrowLeft className="w-4 h-4 mr-1" />
					トップページに戻る
				</Link>

				<div className="rounded-2xl border border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 via-white to-sky-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 p-6 sm:p-8 shadow-sm">
					<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
						<div className="space-y-3">
							<div className="inline-flex items-center gap-2 rounded-full bg-blue-100 dark:bg-blue-900/40 px-3 py-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300">
								<FiHelpCircle className="h-4 w-4" />
								サポートセンター
							</div>
							<h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
								ヘルプセンター
							</h1>
							<p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed max-w-2xl">
								チケット購入、マイページ、主催者機能、当日のトラブル対応まで、このページで必要な情報を一通り確認できます。
							</p>
							<p className="text-xs text-gray-600 dark:text-gray-400">
								最終更新日: 2026年2月7日
							</p>
						</div>
						<div className="rounded-xl border border-blue-200 dark:border-blue-700 bg-white/90 dark:bg-gray-900/60 p-4 sm:p-5 min-w-[260px]">
							<p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
								緊急時の確認順
							</p>
							<ol className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
								<li>1. 購入履歴またはイベント管理画面を確認</li>
								<li>2. ブラウザ再読み込み・再ログインを実施</li>
								<li>3. 解消しない場合は注文情報付きで問い合わせ</li>
							</ol>
						</div>
					</div>
				</div>
			</div>

			<section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
				<a
					href="#first-steps"
					className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
				>
					<p className="text-sm font-semibold text-gray-900 dark:text-white">
						はじめての方へ
					</p>
					<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
						購入者・主催者の導線を確認
					</p>
				</a>
				<a
					href="#faq"
					className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
				>
					<p className="text-sm font-semibold text-gray-900 dark:text-white">
						FAQ
					</p>
					<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
						カテゴリ別によくある質問
					</p>
				</a>
				<a
					href="#troubleshooting"
					className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
				>
					<p className="text-sm font-semibold text-gray-900 dark:text-white">
						トラブル対応
					</p>
					<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
						症状別チェックリスト
					</p>
				</a>
				<a
					href="#contact"
					className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
				>
					<p className="text-sm font-semibold text-gray-900 dark:text-white">
						お問い合わせ
					</p>
					<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
						必要情報と連絡先
					</p>
				</a>
			</section>

			<section id="first-steps" className="space-y-4">
				<div>
					<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
						はじめての方へ
					</h2>
					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
						利用シーンごとに最短の操作ステップを整理しています。
					</p>
				</div>

				<div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
					<div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6">
						<div className="flex items-center gap-2 mb-4">
							<HiTicket className="w-5 h-5 text-blue-600 dark:text-blue-400" />
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
								購入者向けガイド
							</h3>
						</div>
						<ol className="space-y-3">
							{attendeeSteps.map((step, index) => (
								<li key={step.title} className="flex gap-3">
									<div className="h-6 w-6 mt-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold flex items-center justify-center">
										{index + 1}
									</div>
									<div>
										<p className="text-sm font-semibold text-gray-900 dark:text-white">
											{step.title}
										</p>
										<p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
											{step.description}
										</p>
									</div>
								</li>
							))}
						</ol>
						<div className="flex flex-wrap gap-2 mt-5">
							<Link
								to="/events"
								className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
							>
								イベント一覧へ
								<FiExternalLink className="w-3.5 h-3.5" />
							</Link>
							<Link
								to="/mypage"
								className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
							>
								マイページへ
								<FiExternalLink className="w-3.5 h-3.5" />
							</Link>
						</div>
					</div>

					<div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6">
						<div className="flex items-center gap-2 mb-4">
							<FiUsers className="w-5 h-5 text-blue-600 dark:text-blue-400" />
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
								主催者向けガイド
							</h3>
						</div>
						<ol className="space-y-3">
							{organizerSteps.map((step, index) => (
								<li key={step.title} className="flex gap-3">
									<div className="h-6 w-6 mt-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold flex items-center justify-center">
										{index + 1}
									</div>
									<div>
										<p className="text-sm font-semibold text-gray-900 dark:text-white">
											{step.title}
										</p>
										<p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
											{step.description}
										</p>
									</div>
								</li>
							))}
						</ol>
						<div className="flex flex-wrap gap-2 mt-5">
							<Link
								to="/organizer/register"
								className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
							>
								主催者登録へ
								<FiExternalLink className="w-3.5 h-3.5" />
							</Link>
							<Link
								to="/organizer"
								className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
							>
								主催者ダッシュボードへ
								<FiExternalLink className="w-3.5 h-3.5" />
							</Link>
						</div>
					</div>
				</div>
			</section>

			<section id="faq" className="space-y-4">
				<div>
					<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
						よくある質問
					</h2>
					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
						キーワード検索とカテゴリ絞り込みで、必要な回答をすぐに見つけられます。
					</p>
				</div>

				<div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5 space-y-4">
					<div className="relative">
						<FiSearch className="w-4 h-4 text-gray-500 dark:text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
						<input
							type="text"
							value={searchQuery}
							onChange={(event) => setSearchQuery(event.target.value)}
							placeholder="例: 決済エラー / QR / 主催者登録"
							className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 pl-10 pr-4 py-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>

					<div className="flex flex-wrap gap-2">
						<button
							onClick={() => setActiveCategory("all")}
							className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
								activeCategory === "all"
									? "bg-blue-600 text-white"
									: "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
							}`}
						>
							すべて ({faqItems.length})
						</button>
						{helpCategories.map((category) => (
							<button
								key={category.id}
								onClick={() => setActiveCategory(category.id)}
								className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
									activeCategory === category.id
										? "bg-blue-600 text-white"
										: "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
								}`}
							>
								{category.label} ({faqCountByCategory[category.id]})
							</button>
						))}
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
						{helpCategories.map((category) => {
							const Icon = category.icon;
							return (
								<div
									key={category.id}
									className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3"
								>
									<div className="flex items-center gap-2">
										<Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
										<p className="text-sm font-semibold text-gray-900 dark:text-white">
											{category.label}
										</p>
									</div>
									<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
										{category.description}
									</p>
								</div>
							);
						})}
					</div>

					<p className="text-xs text-gray-500 dark:text-gray-400">
						検索結果: {filteredFaqItems.length}件
					</p>

					<div className="space-y-2">
						{filteredFaqItems.length === 0 && (
							<div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center text-sm text-gray-600 dark:text-gray-400">
								該当する質問が見つかりませんでした。キーワードを変更するか、下部のお問い合わせ窓口をご利用ください。
							</div>
						)}

						{filteredFaqItems.map((faq) => {
							const isOpen = openFaqId === faq.id;
							return (
								<div
									key={faq.id}
									className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
								>
									<button
										onClick={() => toggleFaq(faq.id)}
										className="w-full text-left px-4 py-3 sm:px-5 sm:py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/70 transition-colors flex items-start justify-between gap-3"
									>
										<div>
											<p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
												{faq.question}
											</p>
										</div>
										{isOpen ? (
											<FiChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0 mt-0.5" />
										) : (
											<FiChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0 mt-0.5" />
										)}
									</button>

									{isOpen && (
										<div className="px-4 pb-4 sm:px-5 sm:pb-5 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
											<div className="pt-3 space-y-2">
												{faq.answer.map((line, index) => (
													<p
														key={`${faq.id}-${index}`}
														className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
													>
														{line}
													</p>
												))}
											</div>
										</div>
									)}
								</div>
							);
						})}
					</div>
				</div>
			</section>

			<section id="troubleshooting" className="space-y-4">
				<div>
					<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
						トラブルシューティング
					</h2>
					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
						問い合わせ前に確認すると解決率が高い手順を症状別にまとめています。
					</p>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{troubleshootingCards.map((card) => (
						<div
							key={card.title}
							className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5"
						>
							<div className="flex items-center gap-2 mb-3">
								<FiAlertTriangle className="w-4 h-4 text-amber-500" />
								<h3 className="text-base font-semibold text-gray-900 dark:text-white">
									{card.title}
								</h3>
							</div>
							<div className="space-y-3">
								<div>
									<p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
										主な症状
									</p>
									<ul className="space-y-1">
										{card.symptoms.map((symptom) => (
											<li
												key={symptom}
												className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2"
											>
												<FiSmartphone className="w-3.5 h-3.5 mt-0.5 text-gray-400 dark:text-gray-500 shrink-0" />
												{symptom}
											</li>
										))}
									</ul>
								</div>
								<div>
									<p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
										推奨アクション
									</p>
									<ul className="space-y-1">
										{card.actions.map((action) => (
											<li
												key={action}
												className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2"
											>
												<FiCheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-500 shrink-0" />
												{action}
											</li>
										))}
									</ul>
								</div>
							</div>
						</div>
					))}
				</div>

				<div className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 sm:p-5">
					<div className="flex items-start gap-2">
						<FiClock className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
						<div>
							<p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
								イベント当日の入場トラブルについて
							</p>
							<p className="text-sm text-amber-700 dark:text-amber-300 mt-1 leading-relaxed">
								開演直前の対応は会場オペレーションが優先されます。ヘルプ窓口への連絡と並行して、現地スタッフまたは主催者案内に従ってください。
							</p>
						</div>
					</div>
				</div>
			</section>

			<section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6">
				<div className="flex items-center gap-2 mb-3">
					<FiShield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
					<h2 className="text-xl font-bold text-gray-900 dark:text-white">
						安全にご利用いただくために
					</h2>
				</div>
				<ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
					<li className="flex gap-2">
						<FiCheckCircle className="w-4 h-4 mt-0.5 text-green-500 shrink-0" />
						QRチケット画像や注文情報をSNS・メッセージで公開しないでください。
					</li>
					<li className="flex gap-2">
						<FiCheckCircle className="w-4 h-4 mt-0.5 text-green-500 shrink-0" />
						不審な支払い要求やなりすまし連絡は開かず、公式窓口へ確認してください。
					</li>
					<li className="flex gap-2">
						<FiCheckCircle className="w-4 h-4 mt-0.5 text-green-500 shrink-0" />
						共用端末を利用した場合は、終了時にログアウトして閲覧データを削除してください。
					</li>
				</ul>
			</section>

			<section id="contact" className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6 space-y-4">
				<div>
					<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
						お問い合わせ
					</h2>
					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
						解決しない場合は、以下の情報を添えてご連絡ください。
					</p>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
					<div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
						<p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">
							サポート窓口
						</p>
						<a
							href="mailto:info@blackadder.co.jp?subject=b%2B%20ticket%20%E3%81%8A%E5%95%8F%E3%81%84%E5%90%88%E3%82%8F%E3%81%9B"
							className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 transition-colors"
						>
							<FiMail className="w-4 h-4" />
							info@blackadder.co.jp
						</a>
						<p className="text-xs text-blue-700 dark:text-blue-300 mt-3">
							内容を確認のうえ順次返信します。イベント当日は件名に「至急」を含めてください。
						</p>
					</div>

					<div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 p-4">
						<p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
							記載していただきたい情報
						</p>
						<ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-300">
							<li>・発生日時</li>
							<li>・イベント名 / 会場名</li>
							<li>・注文番号（分かる場合）</li>
							<li>・利用端末とブラウザ</li>
							<li>・表示されたメッセージ</li>
							<li>・再現手順とスクリーンショット</li>
						</ul>
					</div>
				</div>

				<div className="flex flex-wrap gap-2 pt-1">
					<Link
						to="/terms-of-service"
						className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
					>
						利用規約
						<FiExternalLink className="w-3.5 h-3.5" />
					</Link>
					<Link
						to="/terms-for-organizers"
						className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
					>
						主催者利用規約
						<FiExternalLink className="w-3.5 h-3.5" />
					</Link>
					<Link
						to="/privacy-policy"
						className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
					>
						プライバシーポリシー
						<FiExternalLink className="w-3.5 h-3.5" />
					</Link>
				</div>
			</section>
		</div>
	);
};

export default HelpPage;
